import { VEHICLE_ORDER, VEHICLES } from '@/entities/vehicles';

/**
 * vehicleCatalog.ts — shared vehicle PRESENTATION data (M68). Moved out of the
 * retired CarSelectScreen so both the online RaceLobbyScreen and the offline
 * GarageScreen draw from one source: livery accents, stat-bar normalisation,
 * personality blurbs, labels.
 */

/** Per-vehicle livery accent — DERIVED from VehicleDef.stockColor since M93
 *  (the same stock paint an undressed car now wears on course), so the garage,
 *  the menus and the races can never drift apart. */
// M151: a ride painted near-black (the muscle convertible) would get an
// invisible accent — its ring, chips and stat bars take polished steel instead
// (its chrome wheels' colour), judged by the stock colour's luminance.
function accentOf(stockColor: number): string {
  const r = (stockColor >> 16) & 0xff;
  const g = (stockColor >> 8) & 0xff;
  const b = stockColor & 0xff;
  const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  if (luma < 0.12) return '#b4b9c4';
  return `#${stockColor.toString(16).padStart(6, '0')}`;
}

export const ACCENTS: Record<string, string> = Object.fromEntries(
  Object.values(VEHICLES).map((v) => [v.id, accentOf(v.stockColor)]),
);

/** Two-line personality per vehicle (M104 upgrade-ladder copy — every unlock
 *  reads as a clear step UP from the ride before it). */
export const BLURBS: Record<string, [string, string]> = {
  coupe: ['Nimble, with glue on the ball.', 'Where every driver starts.'],
  pickup: ['Faster everywhere, and heavy.', 'Bully the pack aside.'],
  muscle: ['Real top end arrives.', 'Raw, unstoppable force.'],
  sports: ['The precision speed machine.', 'Quickest touch on four wheels.'],
  van: ['The hardest launch in the game.', 'Monster pace, monster weight.'],
  motorcycle: ['The apex ride: fastest, stickiest.', 'Weave through anything.'],
  f1: ['Every stat maxed. Nothing above it.', 'The final unlock. The crown.'],
};

/** Display normalisation per stat (bar fill 0..1 across the roster's range).
 *  M104: windows widened to the upgrade ladder's ranges (topSpeed 0.95-1.18,
 *  accel 0.94-1.24, handling 1.1-1.25) so later unlocks don't clamp at a full
 *  bar and the ladder stays visible in the garage. M114: the f1 sits EXACTLY
 *  at every window max (1.2/1.26/1.28/1.6) — full bars on all four by
 *  construction, without moving any other ride's bar. Widening a window means
 *  retuning the f1 row in tuning.vehicles to the new max with it. */
export const STAT_BARS: {
  key: 'topSpeed' | 'accel' | 'handling' | 'weight';
  label: string;
  min: number;
  max: number;
}[] = [
  { key: 'topSpeed', label: 'SPD', min: 0.9, max: 1.2 },
  { key: 'accel', label: 'ACC', min: 0.88, max: 1.26 },
  { key: 'handling', label: 'HDL', min: 1.02, max: 1.28 },
  { key: 'weight', label: 'WGT', min: 0.6, max: 1.6 },
];

export const vehicleLabel = (id: string): string =>
  VEHICLE_ORDER.find((v) => v.id === id)?.label ?? id;
